import { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { useDimensions } from '../../context/ThemeContextManagement';

import { AnimateSplitText } from '../split-text/AnimateSplitText';

// The size the hidden measurer is always rendered at. Text width scales
// linearly with font-size, so one measurement at a known reference size gives
// the exact size needed to fill the container — no iteration required.
const REFERENCE_FONT_SIZE = 60;

// If document.fonts never settles (a font 404, an offline cache miss), reveal
// anyway rather than leaving the heading invisible forever.
const REVEAL_FALLBACK_MS = 300;

// useLayoutEffect warns during SSR; useEffect is the correct no-op there.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export default function TextScaleWidth({
  text = 'Enter your text here',
  animated = false,
  index,
  as: Tag = 'p',
}) {
  const dimensions = useDimensions();
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [fontSize, setFontSize] = useState(REFERENCE_FONT_SIZE);
  // The server can't measure, so the server HTML necessarily carries the
  // reference size. Painting that and then jumping to the fitted size was the
  // entire homepage CLS (0.299 — a 60px heading becoming a 382px one). Instead
  // the text stays `visibility: hidden` until it has a real size, so the first
  // frame it ever paints is the correct one. It occupies its box the whole
  // time, so revealing it shifts nothing. Every call site already animates this
  // in from a hidden state, so nothing is visibly delayed by it.
  const [measured, setMeasured] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    const measureEl = measureRef.current;
    if (!container || !measureEl) return;

    let cancelled = false;

    const recalculate = () => {
      if (cancelled) return;
      // Both widths must be read the same way. getBoundingClientRect is used
      // rather than offsetWidth/scrollWidth because the latter round to whole
      // pixels, which on a long heading is enough to push the fitted text past
      // the container edge. It also reports the *transformed* width, so an
      // ancestor `transform: scale` (HeroHome scales the subheading on scroll)
      // would make a rect-vs-offsetWidth comparison come out 1/scale too big.
      // Measuring both through the same ancestor chain cancels that out.
      const containerWidth = container.getBoundingClientRect().width;
      const textWidth = measureEl.getBoundingClientRect().width;
      if (!containerWidth || !textWidth) return;

      // Floor to 2dp so rounding always errs on the side of fitting.
      const next =
        Math.floor(REFERENCE_FONT_SIZE * (containerWidth / textWidth) * 100) /
        100;
      setFontSize(next);
    };

    const reveal = () => {
      if (!cancelled) setMeasured(true);
    };

    // Runs in a layout effect, so this size is committed before the browser
    // paints the hydrated tree rather than a frame after it.
    recalculate();

    // next/font loads with display: 'swap', so on a cold/slow connection the
    // fallback font (narrower/wider than the real one) can still be showing
    // when we measure above. Re-measure once the real font is active so the
    // size isn't locked in against the wrong glyph widths — and only reveal
    // after that, so the swap can't produce a second shift either.
    const fallbackTimer = setTimeout(reveal, REVEAL_FALLBACK_MS);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        recalculate();
        reveal();
      });
    } else {
      reveal();
    }

    // The container's height changes as `fontSize` changes, so this fires again
    // after every resize. That settles rather than looping: the measurer is
    // fixed at REFERENCE_FONT_SIZE, so as long as the width is unchanged the
    // recomputed value is identical and React bails out of the re-render.
    const resizeObserver = new ResizeObserver(recalculate);
    resizeObserver.observe(container);

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      resizeObserver.disconnect();
    };
  }, [text, dimensions.screenWidth]);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        // The text below is `white-space: nowrap`, so this container's
        // min-content width is the whole unwrapped string. Where this sits
        // directly in a flex or grid parent, that automatic minimum would
        // inflate the box past its track and we'd measure the inflated width.
        minWidth: 0,
        // Sizing the real element by font-size (rather than transform: scale)
        // keeps its layout box the same size as the glyphs it paints, so
        // ancestors can clip, mask and stack against it correctly.
        position: 'relative',
      }}
    >
      <span
        ref={measureRef}
        aria-hidden="true"
        className="h1"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          fontSize: `${REFERENCE_FONT_SIZE}px`,
          lineHeight: '0.765',
          whiteSpace: 'nowrap',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {text}
      </span>
      <Tag
        className="h1"
        style={{
          lineHeight: '0.765',
          fontSize: `${fontSize}px`,
          whiteSpace: 'nowrap',
          textAlign: 'center',
          margin: 0,
          visibility: measured ? 'visible' : 'hidden',
        }}
      >
        {animated ? (
          <AnimateSplitText animationOrder={2} index={index} splitBy="chars">
            {text}
          </AnimateSplitText>
        ) : (
          text
        )}
      </Tag>
    </div>
  );
}
